/* eslint-disable indent */
import React from 'react';
import PropTypes from 'prop-types';
import { makeStyles } from '@material-ui/core/styles';
import { connect, useDispatch } from 'react-redux';
import { get } from 'lodash';
import { createStructuredSelector } from 'reselect';
import AddIcon from '@material-ui/icons/Add';
import { useTranslation } from '../../../../../src/i18n';
import {
  TP,
  MEDIA_TAGS_TYPES_IDS,
  MEDIA_TYPES_IDS,
  MEDIA_ATTRIBUTES_TYPES_IDS,
  PRIVACY_ATTRIBUTE_IDS,
  TAGS_AVAILABILITY_TYPES,
} from '../../../../../constants/consts';
import { colors } from '../../../../../constants/styleConstants';
import Title from '../../../../../components/Title';
import Button from '../../../../../components/Button';
import { updateOne, getList, useBufferValueAction } from '../../../../App/actions';
import { MEDIA, PROFILES_ENDPOINT } from '../../../../../constants/endpointConstants';
import {
  updateProfileInfoAction,
  getArtistBannerImagesAction,
  updateArtistBannerAction,
  deleteImageAction,
} from '../../../actions';
import { selectChosenProfileData, selectLoading, selectBannerImages } from '../../../selectors';
import FbCircularProgress from '../../../../../components/FbCircularProgress';
import ImageCard from '../../../../../components/EditComponents/ImageCard';
import { getImageUrl } from '../../../../../utils/common';
import UploadImageModal from '../../../../../components/EditComponents/UploadImageModal';
import { selectBufferValueData } from '../../../../App/selectors';
import NoListItems from '../../../../../components/NoListItems';
import { EDITING_ENTITY_TYPES, getMediaTagsWithoutEntityTag } from '../../../../../utils/editCommon';
import EditImages from '../../../../EditImages';
import ConfirmDeleteModal from '../../../../../components/EditComponents/ConfirmDeleteModal';
import { selectImageFormatsNames } from '../../../../EditImages/selectors';

const useStyles = makeStyles(theme => ({
  cardsSection: {
    marginBottom: theme.spacing(4),
  },
  profilePhotoSection: {
    marginBottom: theme.spacing(4),
  },
  profilePhoto: {
    minHeight: '3rem',
    maxHeight: 300,
    minWidth: '3rem',
    maxWidth: 400,
    marginRight: theme.spacing(2),
    [theme.breakpoints.down('sm')]: {
      marginRight: 0,
      marginBottom: theme.spacing(2),
    },
  },
  sectionTitle: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: `${theme.spacing(2)}px 0`,
  },
}));

const ImagesTab = ({ goToNextTab, profilePhotoLoading, bannersList, profile, bufferImage, imageFormats }) => {
  const classes = useStyles();
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [isProfileModalOpened, setProfileModalState] = React.useState(null);
  const [isProfileBannerModalOpened, setProfileBannerModalState] = React.useState(null);
  const [photoToDelete, setPhotoToDelete] = React.useState(null);
  const coverImageUrls = get(bannersList, 'data[0].file.urls');

  const getBanner = () => {
    dispatch(
      getList({
        endpoint: MEDIA,
        sagaRoutine: getArtistBannerImagesAction,
        params: {
          queryParams: {
            media_type_id: MEDIA_TYPES_IDS.IMAGE,
            media_tag_type_id: MEDIA_TAGS_TYPES_IDS.PROFILE,
            media_tag_id: profile?.id,
            media_attribute_id: MEDIA_ATTRIBUTES_TYPES_IDS.BANNER,
          },
          pagination: { limit: 1 },
        },
      }),
    );
  };

  React.useEffect(() => {
    getBanner();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const uploadBannerPhoto = file => {
    dispatch(
      updateArtistBannerAction({
        oldBannerId: get(bannersList, 'data[0].id'),
        data: {
          ...(file?.type === 'url' ? { url: file?.src } : { file: { data: `@${file?.file?.name}` } }),
          mediaType: {
            id: MEDIA_TYPES_IDS.IMAGE,
          },
          privacyAttribute: {
            id: PRIVACY_ATTRIBUTE_IDS.PUBLIC,
          },
          tags: [
            {
              tagType: { id: MEDIA_TAGS_TYPES_IDS.PROFILE },
              tagId: profile?.id,
            },
          ],
          title: file?.title,
          attributes: [{ id: MEDIA_ATTRIBUTES_TYPES_IDS.BANNER }],
          availabilityTypes: [TAGS_AVAILABILITY_TYPES.GALLERY],
        },
        files: { [file?.file?.name]: file?.file },
        callback: () => {
          setProfileBannerModalState(false);
          getBanner();
        },
      }),
    );
  };

  const uploadProfilePhoto = fileData => {
    dispatch(
      updateOne({
        endpoint: PROFILES_ENDPOINT,
        sagaRoutine: updateProfileInfoAction,
        id: profile?.id,
        params: {
          image: {
            file: `@${fileData.filename}`,
          },
        },
        files: { [fileData.filename]: fileData.file },
        callback: () => setProfileModalState(false),
      }),
    );
  };

  const closeConfirmationModal = () => {
    setPhotoToDelete(null);
  };

  const deleteImage = () => {
    const photoTags = getMediaTagsWithoutEntityTag(photoToDelete?.tags, MEDIA_TAGS_TYPES_IDS.PROFILE, profile?.id);
    dispatch(
      updateOne({
        endpoint: MEDIA,
        id: photoToDelete?.id,
        sagaRoutine: useBufferValueAction,
        params: {
          tags: photoTags,
        },
        callback: image => {
          dispatch(deleteImageAction(image?.id));
          closeConfirmationModal();
        },
      }),
    );
  };

  return (
    <>
      <section className={classes.profilePhotoSection}>
        <Title>{t(`edit:${TP}.FN_EDITARTIST_PROFILE_PHOTO`)}</Title>
        <ImageCard
          image={profile?.image}
          editFunc={() => {
            setProfileModalState(true);
          }}
          contentClassName={classes.profilePhoto}
          loading={profilePhotoLoading}
        />
      </section>
      <section className={classes.profilePhotoSection}>
        <div className={classes.sectionTitle}>
          <Title>{t(`${TP}.FN_EDITARTIST_PROFILE_BANNER`)}</Title>
          {!coverImageUrls && (
            <Button
              onClick={() => setProfileBannerModalState(true)}
              color="default"
              variant="outlined"
              endIcon={<AddIcon style={{ color: colors.blue }} />}
            >
              {t(`${TP}.FN_ADD_COVER_PHOTO`)}
            </Button>
          )}
        </div>
        {coverImageUrls ? (
          <ImageCard
            image={coverImageUrls}
            editFunc={() => {
              setProfileBannerModalState(true);
            }}
            contentClassName={classes.profilePhoto}
            loading={bannersList?.loading}
            deleteFunc={() => setPhotoToDelete({ ...get(bannersList, 'data[0]'), imageType: 'banner' })}
          />
        ) : (
          <NoListItems text={t(`${TP}.FN_NO_COVER_PHOTO`)} />
        )}
      </section>
      <EditImages type={EDITING_ENTITY_TYPES.ARTIST} entity={profile} title={`${TP}.FN_GALLERY_PHOTOS`} />
      <Button onClick={goToNextTab}>{t(`${TP}.m_NEXTSTEP`)}</Button>
      {isProfileModalOpened && (
        <UploadImageModal
          onClose={() => setProfileModalState(false)}
          imageUrl={profile.image ? getImageUrl(profile.image) : ''}
          submitImage={uploadProfilePhoto}
          isLoading={profilePhotoLoading}
          formats={imageFormats}
        />
      )}
      {isProfileBannerModalOpened && (
        <UploadImageModal
          onClose={() => setProfileBannerModalState(false)}
          imageUrl={coverImageUrls ? getImageUrl(coverImageUrls) : ''}
          submitImage={uploadBannerPhoto}
          isLoading={bufferImage?.loading}
          formats={imageFormats}
        />
      )}
      {photoToDelete && (
        <ConfirmDeleteModal
          closeModal={closeConfirmationModal}
          title={t(`${TP}.FN_DELETE_ARTIST_IMAGE_CONFIRMATION`)}
          onDelete={deleteImage}
        >
          {bufferImage?.loading && <FbCircularProgress />}
        </ConfirmDeleteModal>
      )}
    </>
  );
};

ImagesTab.propTypes = {
  goToNextTab: PropTypes.func.isRequired,
  profile: PropTypes.object,
  imageFormats: PropTypes.array,
};

const mapStateToProps = createStructuredSelector({
  profilePhotoLoading: selectLoading(),
  bannersList: selectBannerImages(),
  profile: selectChosenProfileData(),
  bufferImage: selectBufferValueData(),
  imageFormats: selectImageFormatsNames(),
});

const withConnect = connect(mapStateToProps, null);

export default withConnect(ImagesTab);
